import { useState, useEffect } from 'react';
import { Loader2, Lock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/use-subscription';

const accountColors = [
  "#10b981", "#3b82f6", "#8b5cf6", "#f59e0b",
  "#ef4444", "#ec4899", "#14b8a6", "#f97316"
];

interface Account {
  id: string;
  name: string;
  type: 'checking' | 'savings' | 'credit_card' | 'cash' | 'investment';
  institution: string | null;
  balance: number;
  credit_limit: number | null;
  closing_day: number | null;
  due_day: number | null;
  color: string;
  archived?: boolean;
}

interface AccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account?: Account | null;
  onSuccess?: () => void;
  accountCounts?: { checking: number; savings: number; credit_card: number };
}

export function AccountModal({ open, onOpenChange, account, onSuccess, accountCounts: propCounts }: AccountModalProps) {
  const [loading, setLoading] = useState(false);
  const [countsLoading, setCountsLoading] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [type, setType] = useState<string>('');
  const [institution, setInstitution] = useState('');
  const [balance, setBalance] = useState('');
  const [creditLimit, setCreditLimit] = useState('');
  const [closingDay, setClosingDay] = useState('');
  const [dueDay, setDueDay] = useState('');
  const [color, setColor] = useState(accountColors[0]);
  const [isArchived, setIsArchived] = useState(false);

  // Account counts for limit checking - use prop if available, otherwise fetch
  const [fetchedCounts, setFetchedCounts] = useState<{ checking: number; savings: number; credit_card: number } | null>(null);
  const accountCounts = propCounts ?? fetchedCounts ?? { checking: 999, savings: 999, credit_card: 999 };

  const { isWithinLimit, canAccess } = useSubscription();

  const isEditing = !!account;
  const isCreditCard = type === 'credit_card';

  // Check which types are locked (default to locked until counts are loaded)
  const countsReady = propCounts != null || fetchedCounts != null;
  const checkingLocked = !countsReady || !isWithinLimit('max_checking_accounts', accountCounts.checking);
  const savingsLocked = !countsReady || !isWithinLimit('max_savings_accounts', accountCounts.savings);
  const creditCardLocked = !countsReady || !canAccess('credit_card_access') || !isWithinLimit('max_credit_cards', accountCounts.credit_card);

  // Reset form when modal opens/closes or account changes
  useEffect(() => {
    if (open) {
      if (account) {
        setName(account.name);
        setType(account.type);
        setInstitution(account.institution || '');
        setBalance(account.balance.toString());
        setCreditLimit(account.credit_limit?.toString() || '');
        setClosingDay(account.closing_day?.toString() || '');
        setDueDay(account.due_day?.toString() || '');
        setColor(account.color);
        setIsArchived(account.archived ?? false);
      } else {
        setName('');
        setType('');
        setInstitution('');
        setBalance('');
        setCreditLimit('');
        setClosingDay('');
        setDueDay('');
        setColor(accountColors[0]);
        setIsArchived(false);
      }
      // Only fetch counts if not provided via props
      if (!propCounts) {
        fetchAccountCounts();
      }
    } else {
      setFetchedCounts(null);
    }
  }, [open, account, propCounts]);

  const fetchAccountCounts = async () => {
    setCountsLoading(true);
    try {
      const { data } = await supabase
        .from('accounts')
        .select('type')
        .eq('is_active', true)
        .eq('archived', false);

      if (data) {
        const counts = { checking: 0, savings: 0, credit_card: 0 };
        data.forEach(a => {
          if (a.type === 'checking') counts.checking++;
          else if (a.type === 'savings') counts.savings++;
          else if (a.type === 'credit_card') counts.credit_card++;
        });
        setFetchedCounts(counts);
      }
    } finally {
      setCountsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!name || !type) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Preencha o nome e tipo da conta.',
      });
      return;
    }

    // Double-check limits on submit with fresh DB count (for new accounts only)
    if (!isEditing) {
      const { data: freshAccounts } = await supabase
        .from('accounts')
        .select('type')
        .eq('is_active', true)
        .eq('archived', false);

      const freshCounts = { checking: 0, savings: 0, credit_card: 0 };
      (freshAccounts || []).forEach(a => {
        if (a.type === 'checking') freshCounts.checking++;
        else if (a.type === 'savings') freshCounts.savings++;
        else if (a.type === 'credit_card') freshCounts.credit_card++;
      });

      const typeBlocked =
        (type === 'checking' && !isWithinLimit('max_checking_accounts', freshCounts.checking)) ||
        (type === 'savings' && !isWithinLimit('max_savings_accounts', freshCounts.savings)) ||
        (type === 'credit_card' && (!canAccess('credit_card_access') || !isWithinLimit('max_credit_cards', freshCounts.credit_card)));

      if (typeBlocked) {
        toast({
          variant: 'destructive',
          title: 'Limite atingido',
          description: 'Você atingiu o limite do seu plano para este tipo de conta. Faça upgrade para criar mais.',
        });
        return;
      }
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const accountData = {
        name,
        type: type as Account['type'],
        institution: institution || null,
        balance: parseFloat(balance) || 0,
        color,
        ...(isCreditCard && {
          credit_limit: parseFloat(creditLimit) || null,
          closing_day: parseInt(closingDay) || null,
          due_day: parseInt(dueDay) || null,
        }),
      };

      if (isEditing) {
        const { error } = await supabase
          .from('accounts')
          .update({ ...accountData, archived: isArchived })
          .eq('id', account.id);

        if (error) throw error;

        toast({
          title: 'Conta atualizada!',
          description: 'As alterações foram salvas com sucesso.',
        });
      } else {
        const { error } = await supabase
          .from('accounts')
          .insert({
            user_id: user.id,
            ...accountData,
          });

        if (error) throw error;

        toast({
          title: 'Conta criada!',
          description: 'Sua nova conta foi criada com sucesso.',
        });
      }

      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: isEditing ? 'Erro ao atualizar conta' : 'Erro ao criar conta',
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate day options (1-31)
  const dayOptions = Array.from({ length: 31 }, (_, i) => i + 1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Conta' : 'Nova Conta'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome da Conta *</Label>
            <Input
              id="name"
              placeholder="Ex: Conta Corrente Principal"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">Tipo de Conta *</Label>
            <Select value={type} onValueChange={setType} disabled={isEditing}>
              <SelectTrigger id="type">
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="checking" disabled={!isEditing && checkingLocked}>
                  <span className="flex items-center gap-2">
                    Conta Corrente
                    {!isEditing && checkingLocked && <Lock className="h-3 w-3 text-muted-foreground" />}
                  </span>
                </SelectItem>
                <SelectItem value="savings" disabled={!isEditing && savingsLocked}>
                  <span className="flex items-center gap-2">
                    Poupança
                    {!isEditing && savingsLocked && <Lock className="h-3 w-3 text-muted-foreground" />}
                  </span>
                </SelectItem>
                <SelectItem value="credit_card" disabled={!isEditing && creditCardLocked}>
                  <span className="flex items-center gap-2">
                    Cartão de Crédito
                    {!isEditing && creditCardLocked && <Lock className="h-3 w-3 text-muted-foreground" />}
                  </span>
                </SelectItem>
                <SelectItem value="cash">Dinheiro</SelectItem>
                <SelectItem value="investment">Investimentos</SelectItem>
              </SelectContent>
            </Select>
            {isEditing && (
              <p className="text-xs text-muted-foreground">
                O tipo da conta não pode ser alterado
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="institution">Banco/Instituição</Label>
            <Input
              id="institution"
              placeholder="Ex: Nubank, Itaú, C6 Bank..."
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
            />
          </div>

          {isCreditCard ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="credit-limit">Limite de Crédito</Label>
                <Input
                  id="credit-limit"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={creditLimit}
                  onChange={(e) => setCreditLimit(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="closing-day">Dia de Fechamento *</Label>
                  <Select value={closingDay} onValueChange={setClosingDay}>
                    <SelectTrigger id="closing-day">
                      <SelectValue placeholder="Dia" />
                    </SelectTrigger>
                    <SelectContent>
                      {dayOptions.map((day) => (
                        <SelectItem key={day} value={day.toString()}>
                          {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Dia que a fatura fecha
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="due-day">Dia de Vencimento *</Label>
                  <Select value={dueDay} onValueChange={setDueDay}>
                    <SelectTrigger id="due-day">
                      <SelectValue placeholder="Dia" />
                    </SelectTrigger>
                    <SelectContent>
                      {dayOptions.map((day) => (
                        <SelectItem key={day} value={day.toString()}>
                          {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Dia que a fatura vence
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="balance">
                {isEditing ? 'Saldo Atual' : 'Saldo Inicial'}
              </Label>
              <Input
                id="balance"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={balance}
                onChange={(e) => setBalance(e.target.value)}
              />
            </div>
          )}

          {isEditing && (
            <div className="space-y-2 pt-2 border-t">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Arquivar conta</Label>
                  <p className="text-xs text-muted-foreground">
                    Conta arquivada não aparece no dashboard, filtros e selects.
                    Transações históricas são mantidas.
                  </p>
                </div>
                <Switch
                  checked={isArchived}
                  onCheckedChange={setIsArchived}
                  aria-label="Arquivar conta"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Cor de Identificação</Label>
            <div className="flex gap-2 flex-wrap">
              {accountColors.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`h-9 w-9 rounded-full border-2 transition-all ${
                    color === c ? 'border-foreground scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name || !type || (isCreditCard && (!closingDay || !dueDay)) || loading}
            className="flex-1"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEditing ? 'Salvando...' : 'Criando...'}
              </>
            ) : (
              isEditing ? 'Salvar Alterações' : 'Criar Conta'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
